import { Link, useRouter, useRouterState } from "@tanstack/react-router";
import { DateNav } from "./DateNav";
import { TimeControl } from "./TimeControl";
import { SiteTabs } from "./SiteTabs";
import { FamilyTabs } from "./FamilyTabs";
import type { Mode, Period } from "../router/search";

export interface Crumb {
  label: string;
  to?: string;
}

/** Which time control the screen wants in its header. */
import { BackButton } from "./BackButton";

export type HeaderTimeControl = "period" | "periods" | "mode" | "none";

interface PageHeaderProps {
  title: string;
  scope?: string;
  crumbs?: Crumb[];
  action?: React.ReactNode;
  /** "period" (default): period tabs + mode chips · "periods": period tabs ALONE,
   *  for a screen that already states its own live state · "mode": Live/Replay/
   *  Forecast only · "none": nothing. */
  timeControl?: HeaderTimeControl;
  /** @deprecated use timeControl="none" */
  showTimeControl?: boolean;
  timeOptions?: string[];
  site?: string;
  tenant?: string;
  scopeId?: string;
  family?: string;
  showDateNav?: boolean;
  /** Stack the scope line under the title instead of on its baseline (default is inline). */
  stackScope?: boolean;
}

export function PageHeader({
  title,
  scope,
  crumbs,
  action,
  timeControl = "period",
  showTimeControl,
  timeOptions,
  site,
  tenant,
  scopeId,
  family,
  showDateNav,
  stackScope,
}: PageHeaderProps) {
  const hasSiteNav = !!(site && tenant && scopeId);
  // Back-compat: showTimeControl={false} is the same as timeControl="none".
  const resolvedTimeControl: HeaderTimeControl = showTimeControl === false ? "none" : timeControl;

  // A visible way back, on every screen that has one.
  //
  // The browser's own Back now works (the discrete controls push instead of
  // replacing), but there was no way back ON THE PAGE at all: `crumbs` is
  // implemented right here and NO screen passes it, so a reader who opened, say,
  // Meters & sensors had nothing to click to leave it. Putting the control in the
  // shared header fixes every screen at once, rather than waiting for 37 screens to
  // each adopt breadcrumbs.
  //
  // It steps the history rather than guessing a parent route, so it always returns
  // the reader to where they actually came from. Hidden at the root (nothing above
  // it) and when this is the first entry in the session's history, so it is never a
  // control that does nothing.
  //
  // Deliberately NOT `useRouter()`: this header is rendered outside a RouterProvider
  // by the screen tests, where that hook throws and took the whole header down with
  // it. The browser history IS the router's history here, so reading it directly is
  // both equivalent and safe in every mounting context.
  return (
    <div>
      <div style={headStyle}>
        <BackButton />
        <div
          style={
            stackScope
              ? { display: "flex", flexDirection: "column", gap: 2 }
              : { display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }
          }
        >
          {crumbs && crumbs.length > 0 ? (
            <nav aria-label="Page breadcrumb" style={crumbRowStyle}>
              {crumbs.map((c, i) => (
                <span key={i} style={crumbItemStyle}>
                  {i > 0 ? <span style={crumbSep}>/</span> : null}
                  {c.to ? (
                    <Link to={c.to as never} style={crumbAnchor}>{c.label}</Link>
                  ) : (
                    <span style={crumbCurrent}>{c.label}</span>
                  )}
                </span>
              ))}
            </nav>
          ) : null}
          <h1 style={titleStyle}>{title}</h1>
          {scope ? <span style={scopeStyle}>{scope}</span> : null}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {resolvedTimeControl !== "none" ? (
            <TimeControl visiblePeriods={timeOptions} variant={resolvedTimeControl} />
          ) : null}
          {showDateNav ? <DateNavConnected /> : null}
          {action ?? null}
        </div>
      </div>

      {hasSiteNav && (
        <SiteTabs tenant={tenant} scope={scopeId} site={site} />
      )}

      {family && (
        <FamilyTabs family={family} tenant={tenant} scope={scopeId} site={site} />
      )}
    </div>
  );
}

function DateNavConnected() {
  const router = useRouter();
  const matches = useRouterState({ select: (s) => s.matches });
  const leaf = matches[matches.length - 1]?.search as Record<string, unknown> | undefined;
  const period = leaf?.period as Period | undefined;
  const mode = (leaf?.mode as Mode | undefined) ?? "live";
  const from = typeof leaf?.from === "string" ? leaf.from : undefined;
  const to = typeof leaf?.to === "string" ? leaf.to : undefined;

  if (!period) return null;

  return (
    <DateNav
      period={period}
      mode={mode}
      from={from}
      to={to}
      onNavigate={(newFrom, newTo) =>
        // Stepping or picking a window IS a custom range — set period=custom with
        // it, so screens that honour from/to only for custom (e.g. the fleet grid)
        // actually move. Mirrors Home's onWindow. Without this, period stayed
        // "today"/"7d" and from/to were ignored, so the arrows did nothing.
        // PUSH: stepping the date is a decision, so Back returns to the previous
        // window rather than leaving the screen (QA, 25 Oct).
        void router.navigate({
          search: ((prev: Record<string, unknown>) => ({
            ...prev, period: "custom", from: newFrom, to: newTo,
          })) as never,
        })
      }
    />
  );
}

const headStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "14px 0 6px",
};

const titleStyle: React.CSSProperties = {
  fontSize: "var(--sk-type-title-size, 18px)",
  fontWeight: 600,
  margin: 0,
  lineHeight: 1.3,
  color: "var(--sk-color-ink-1)",
};

const scopeStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--sk-color-ink-3)",
};

const crumbRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 0,
  fontSize: 12,
  color: "var(--sk-color-ink-3)",
};

const crumbItemStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
};

const crumbSep: React.CSSProperties = {
  margin: "0 6px",
  color: "var(--sk-color-ink-3)",
};


const crumbAnchor: React.CSSProperties = {
  color: "var(--sk-color-ink-2)",
  textDecoration: "none",
  cursor: "pointer",
};

const crumbCurrent: React.CSSProperties = {
  color: "var(--sk-color-ink-1)",
  fontWeight: 500,
};
